import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import SendIcon from '@mui/icons-material/Send';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import ViewDayOutlinedIcon from '@mui/icons-material/ViewDayOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import api from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import AlertModal from '../components/AlertModal';
import ConfirmModal from '../components/ConfirmModal';
import PremiumSelect from '../components/PremiumSelect';
import PageContainer from '../components/ui/PageContainer';
import StatusChip from '../components/ui/StatusChip';
import HolidayCalendar from '../components/HolidayCalendar';
import { getUserPhotoUrl } from '../utils/userPhoto';
import { AlertState, StandupEntry, User } from '../types';

interface DraftState {
  done: string;
  next: string;
  blockers: string;
}

const SURFACE_SX = { p: { xs: 2, md: 3 }, border: '1px solid', borderColor: 'divider' } as const;

const KICKER_SX = {
  display: 'block',
  fontSize: '0.72rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color: 'text.secondary',
  mb: 0.5,
} as const;

const todayKey = (): string => {
  const now = new Date();
  const brazil = new Date(now.getTime() - 3 * 60 * 60 * 1000);
  return brazil.toISOString().slice(0, 10);
};

const emptyDraft = (): DraftState => ({ done: '', next: '', blockers: '' });

const formatDayLong = (value: string): string => {
  if (!value || value.length < 10) return value;
  const [y, m, d] = value.slice(0, 10).split('-').map(Number);
  const date = new Date(y, m - 1, d);
  const formatted = date.toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
};

const initials = (name?: string): string => {
  if (!name) return '?';
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');
};

const Standups: React.FC = () => {
  const authContext = useContext(AuthContext);
  const { user } = authContext || {};
  const canSeeTeam = user?.role === 'admin' || user?.role === 'hr' || user?.role === 'manager';

  const [entries, setEntries] = useState<StandupEntry[]>([]);
  const [mine, setMine] = useState<StandupEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState<AlertState>({ isOpen: false, message: '', type: 'error' });

  // Compose: selected date (controlled) + fields.
  const [selectedDate, setSelectedDate] = useState<string>(todayKey);
  const [draft, setDraft] = useState<DraftState>(emptyDraft);

  const [users, setUsers] = useState<User[]>([]);
  const [filterUserId, setFilterUserId] = useState<string>('');

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<DraftState>(emptyDraft);
  const [toDelete, setToDelete] = useState<StandupEntry | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadEntries = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const params = filterUserId ? { userId: filterUserId } : {};
      const [feed, mineRes] = await Promise.all([
        api.get<StandupEntry[]>('/standups', { params }),
        api.get<StandupEntry[]>('/standups/mine'),
      ]);
      setEntries(feed.data);
      setMine(mineRes.data);
    } catch {
      setAlert({ isOpen: true, message: 'Não foi possível carregar os registros.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [filterUserId]);

  useEffect(() => {
    void loadEntries();
  }, [loadEntries]);

  useEffect(() => {
    if (!canSeeTeam) return;
    let active = true;
    api
      .get<User[]>('/users')
      .then((res) => {
        if (active) setUsers(res.data.filter((u) => u.active));
      })
      .catch(() => {
        /* filter is optional; ignore load errors */
      });
    return () => {
      active = false;
    };
  }, [canSeeTeam]);

  // My existing entry for the currently selected date (compose = upsert by day).
  const existingForDate = useMemo(
    () => mine.find((e) => e.entryDate.slice(0, 10) === selectedDate) ?? null,
    [mine, selectedDate],
  );

  // When the date changes, prefill the form with the existing entry (if any).
  useEffect(() => {
    if (existingForDate) {
      setDraft({
        done: existingForDate.done,
        next: existingForDate.next ?? '',
        blockers: existingForDate.blockers ?? '',
      });
    } else {
      setDraft(emptyDraft());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, existingForDate?.id]);

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (!draft.done.trim()) {
      setAlert({ isOpen: true, message: 'Descreva o que você fez.', type: 'warning' });
      return;
    }
    setSaving(true);
    try {
      await api.post('/standups', {
        entryDate: selectedDate,
        done: draft.done.trim(),
        next: draft.next.trim() || undefined,
        blockers: draft.blockers.trim() || undefined,
      });
      setAlert({
        isOpen: true,
        message: existingForDate ? 'Registro atualizado!' : 'Registro salvo!',
        type: 'success',
      });
      await loadEntries();
    } catch {
      setAlert({ isOpen: true, message: 'Não foi possível salvar o registro.', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (entry: StandupEntry): void => {
    setEditingId(entry.id);
    setEditDraft({
      done: entry.done,
      next: entry.next ?? '',
      blockers: entry.blockers ?? '',
    });
  };

  const saveEdit = async (id: string): Promise<void> => {
    if (!editDraft.done.trim()) {
      setAlert({ isOpen: true, message: 'Descreva o que você fez.', type: 'warning' });
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/standups/${id}`, {
        done: editDraft.done.trim(),
        next: editDraft.next.trim(),
        blockers: editDraft.blockers.trim(),
      });
      setEditingId(null);
      await loadEntries();
    } catch {
      setAlert({ isOpen: true, message: 'Não foi possível atualizar o registro.', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async (): Promise<void> => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/standups/${toDelete.id}`);
      setToDelete(null);
      await loadEntries();
    } catch {
      setAlert({ isOpen: true, message: 'Não foi possível remover o registro.', type: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const userOptions = useMemo(
    () => [
      { value: '', label: 'Toda a equipe' },
      ...users.map((u) => ({ value: u.id, label: u.name })),
    ],
    [users],
  );

  // Group entries by calendar day for the timeline.
  const grouped = useMemo(() => {
    const map = new Map<string, StandupEntry[]>();
    for (const entry of entries) {
      const key = entry.entryDate.slice(0, 10);
      const bucket = map.get(key) ?? [];
      bucket.push(entry);
      map.set(key, bucket);
    }
    return Array.from(map.entries()).sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [entries]);

  const canEdit = (entry: StandupEntry): boolean =>
    entry.user?.id === user?.id || user?.role === 'admin';

  const isToday = selectedDate === todayKey();

  return (
    <PageContainer
      title="Stand-up diário"
      subtitle="Registre o que você fez a cada dia — como uma daily. Acompanhe o histórico do time."
      icon={<ViewDayOutlinedIcon />}
      maxWidth="xl"
    >
      <Grid container spacing={3}>
        {/* Compose */}
        <Grid size={{ xs: 12, md: 5, lg: 4 }}>
          <Paper
            component="form"
            onSubmit={handleSubmit}
            sx={{ ...SURFACE_SX, position: { md: 'sticky' }, top: { md: 88 } }}
          >
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
              <Typography component="span" sx={KICKER_SX}>
                Novo registro
              </Typography>
              <StatusChip
                tone={existingForDate ? 'info' : isToday ? 'accent' : 'neutral'}
                label={existingForDate ? 'Editando o dia' : isToday ? 'Hoje' : 'Data escolhida'}
              />
            </Stack>

            {/* App calendar as the date picker */}
            <Box sx={{ mb: 2 }}>
              <HolidayCalendar
                pickerOnly
                compact
                selectedDate={selectedDate}
                onSelectDate={setSelectedDate}
              />
            </Box>

            <Typography sx={{ fontWeight: 800, fontSize: '1rem', mb: 1.5, textTransform: 'capitalize' }}>
              {formatDayLong(selectedDate)}
            </Typography>

            <Stack spacing={2}>
              <TextField
                label="Concluído"
                placeholder="Hoje eu fiz: x, y, z..."
                value={draft.done}
                onChange={(e) => setDraft((c) => ({ ...c, done: e.target.value }))}
                fullWidth
                required
                multiline
                minRows={3}
              />
              <TextField
                label="Próximos passos"
                placeholder="O que vem a seguir (opcional)"
                value={draft.next}
                onChange={(e) => setDraft((c) => ({ ...c, next: e.target.value }))}
                fullWidth
                multiline
                minRows={2}
              />
              <TextField
                label="Impedimentos"
                placeholder="Bloqueios / dependências (opcional)"
                value={draft.blockers}
                onChange={(e) => setDraft((c) => ({ ...c, blockers: e.target.value }))}
                fullWidth
                multiline
                minRows={2}
              />
              <Button
                type="submit"
                variant="contained"
                startIcon={saving ? <CircularProgress size={18} color="inherit" /> : existingForDate ? <SaveIcon /> : <SendIcon />}
                disabled={saving}
              >
                {saving ? 'Salvando...' : existingForDate ? 'Atualizar registro' : 'Registrar'}
              </Button>
              <Typography variant="caption" color="text.secondary">
                Um registro por dia — enviar de novo na mesma data atualiza o existente.
              </Typography>
            </Stack>
          </Paper>
        </Grid>

        {/* Timeline */}
        <Grid size={{ xs: 12, md: 7, lg: 8 }}>
          <Paper sx={SURFACE_SX}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between', mb: 2 }}
            >
              <Box>
                <Typography component="span" sx={KICKER_SX}>
                  {canSeeTeam ? 'Histórico da equipe' : 'Meu histórico'}
                </Typography>
                <Typography sx={{ fontWeight: 800, fontSize: '1.05rem' }}>
                  Linha do tempo
                </Typography>
              </Box>
              {canSeeTeam && (
                <Box sx={{ minWidth: { sm: 240 } }}>
                  <PremiumSelect
                    label="Colaborador"
                    value={filterUserId}
                    options={userOptions}
                    onChange={setFilterUserId}
                  />
                </Box>
              )}
            </Stack>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : grouped.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
                <ViewDayOutlinedIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                <Typography color="text.secondary">
                  Nenhum registro ainda. Conte o que você fez hoje!
                </Typography>
              </Box>
            ) : (
              <Stack spacing={3}>
                {grouped.map(([day, dayEntries]) => (
                  <Box key={day}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
                      <Typography component="span" sx={{ ...KICKER_SX, mb: 0, textTransform: 'capitalize' }}>
                        {formatDayLong(day)}
                      </Typography>
                      <Divider sx={{ flexGrow: 1 }} />
                      <StatusChip tone="neutral" label={dayEntries.length} />
                    </Stack>
                    <Stack spacing={1.5}>
                      {dayEntries.map((entry) => {
                        const isEditing = editingId === entry.id;
                        return (
                          <Paper key={entry.id} variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, border: 1, borderColor: 'divider' }}>
                            <Stack
                              direction="row"
                              spacing={2}
                              sx={{ alignItems: 'flex-start', justifyContent: 'space-between' }}
                            >
                              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', minWidth: 0 }}>
                                <Avatar
                                  src={getUserPhotoUrl(entry.user) || undefined}
                                  sx={{ width: 40, height: 40, bgcolor: 'primary.main', fontWeight: 700 }}
                                >
                                  {initials(entry.user?.name)}
                                </Avatar>
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                    {entry.user?.name ?? 'Colaborador'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {entry.user?.id === user?.id ? 'Você' : entry.user?.email}
                                  </Typography>
                                </Box>
                              </Box>
                              {canEdit(entry) && !isEditing && (
                                <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                                  <Tooltip title="Editar">
                                    <IconButton size="small" onClick={() => startEdit(entry)}>
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Remover">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => setToDelete(entry)}
                                    >
                                      <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Stack>
                              )}
                            </Stack>

                            <Divider sx={{ my: 1.5 }} />

                            {isEditing ? (
                              <Stack spacing={1.5}>
                                <TextField
                                  label="Concluído"
                                  value={editDraft.done}
                                  onChange={(e) => setEditDraft((d) => ({ ...d, done: e.target.value }))}
                                  fullWidth
                                  multiline
                                  minRows={2}
                                />
                                <TextField
                                  label="Próximos passos"
                                  value={editDraft.next}
                                  onChange={(e) => setEditDraft((d) => ({ ...d, next: e.target.value }))}
                                  fullWidth
                                  multiline
                                  minRows={2}
                                />
                                <TextField
                                  label="Impedimentos"
                                  value={editDraft.blockers}
                                  onChange={(e) => setEditDraft((d) => ({ ...d, blockers: e.target.value }))}
                                  fullWidth
                                  multiline
                                  minRows={2}
                                />
                                <Stack direction="row" spacing={1}>
                                  <Button
                                    variant="contained"
                                    startIcon={<SaveIcon />}
                                    onClick={() => saveEdit(entry.id)}
                                    disabled={saving}
                                  >
                                    Salvar
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    startIcon={<CloseIcon />}
                                    onClick={() => setEditingId(null)}
                                    disabled={saving}
                                  >
                                    Cancelar
                                  </Button>
                                </Stack>
                              </Stack>
                            ) : (
                              <Stack spacing={1.25}>
                                <StandupField
                                  icon={<CheckCircleOutlineIcon fontSize="small" color="success" />}
                                  label="Concluído"
                                  text={entry.done}
                                />
                                {entry.next && (
                                  <StandupField
                                    icon={<ArrowForwardOutlinedIcon fontSize="small" color="info" />}
                                    label="Próximos passos"
                                    text={entry.next}
                                  />
                                )}
                                {entry.blockers && (
                                  <StandupField
                                    icon={<BlockOutlinedIcon fontSize="small" color="error" />}
                                    label="Impedimentos"
                                    text={entry.blockers}
                                  />
                                )}
                              </Stack>
                            )}
                          </Paper>
                        );
                      })}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}
          </Paper>
        </Grid>
      </Grid>

      <AlertModal
        isOpen={alert.isOpen}
        onClose={() => setAlert((a) => ({ ...a, isOpen: false }))}
        message={alert.message}
        type={alert.type}
      />
      <ConfirmModal
        isOpen={Boolean(toDelete)}
        title="Remover registro"
        message="Tem certeza que deseja remover este registro de stand-up?"
        confirmLabel="Remover"
        tone="danger"
        confirmDisabled={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setToDelete(null)}
      />
    </PageContainer>
  );
};

interface StandupFieldProps {
  icon: React.ReactNode;
  label: string;
  text: string;
}

const StandupField: React.FC<StandupFieldProps> = ({ icon, label, text }) => (
  <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
    <Box sx={{ mt: '2px', flexShrink: 0, display: 'inline-flex' }}>{icon}</Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        sx={{ fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}
      >
        {label}
      </Typography>
      <Typography sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{text}</Typography>
    </Box>
  </Box>
);

export default Standups;
